import {
  ExclamationTriangleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/outline';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import {
  ActionIcon,
  Image,
  Loader,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { VolumeDownRounded } from '@mui/icons-material';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { synthesizeSpeech, TTSError } from 'src/services/humeTts';
import { isImageMessageJSON } from 'src/utils/helpers';
import Icon from '../common/Icons/Icons/Icon';
import styles from './BotMessage.module.css';

const WaveformAnimation = () => (
  <div className={styles.waveformContainer}>
    {/* Left outer bar - fixed */}
    <span className={`${styles.waveBar} ${styles.outer}`} />

    {/* Center bars - mirrored animation */}
    <span
      className={`${styles.waveBar} ${styles.center} ${styles.centerLeft}`}
    />
    <span
      className={`${styles.waveBar} ${styles.center} ${styles.centerRight}`}
    />

    {/* Right outer bar - fixed */}
    <span className={`${styles.waveBar} ${styles.outer}`} />
  </div>
);

interface BotMessage {
  message: string;
  index: number;
  isLatest?: boolean;
  streaming?: boolean;
  traceless?: boolean;
  toggleOptions?: () => void;
  closeOptions?: () => void;
  showOptions?: boolean;
  image_url?: string;
}

const BotMessage = (props: BotMessage) => {
  const {
    streaming,
    isLatest,
    traceless,
    toggleOptions = () => {},
    closeOptions = () => {},
    showOptions,
    image_url,
  } = props;
  const theme = useMantineTheme();
  const [latest, setLatest] = useState(isLatest);
  const [orating, setOrating] = useState<
    false | 'loading' | 'playing' | 'paused' | 'error'
  >(false);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const clipboard = useClipboard({ timeout: 500 });

  // Clear error after 5 seconds
  useEffect(() => {
    if (ttsError) {
      const timer = setTimeout(() => setTtsError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [ttsError]);

  useEffect(() => {
    setLatest(isLatest);
  }, [isLatest]);

  useEffect(() => {
    if (showOptions) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          lastMessageRef.current &&
          !lastMessageRef.current.contains(event.target as Node)
        ) {
          closeOptions();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptions, closeOptions]);

  const handleCopy = (textToCopy: string) => {
    clipboard.copy(textToCopy);
    showNotification({
      message: 'Text copied to clipboard',
      color: 'grape',
    });
  };

  const togglePlayback = useCallback(async () => {
    if (!audioRef.current) {
      await orateText(props.message);
      return;
    }

    if (orating === 'playing') {
      audioRef.current.pause();
      setOrating('paused');
    } else {
      await audioRef.current.play();
      setOrating('playing');
    }
  }, [orating, props.message]);

  const orateText = async (text: string) => {
    if (orating === 'loading') return;

    try {
      setOrating('loading');
      setTtsError(null);

      const audioB64 = await synthesizeSpeech(text);
      const audio = new Audio(`data:audio/wav;base64,${audioB64}`);
      audio.preload = 'auto';
      audioRef.current = audio;

      audio.oncanplaythrough = () => {
        setOrating('playing');
        audio
          .play()
          .then(() => setOrating('playing'))
          .catch((err) => {
            console.error('Audio playback error:', err);
            setOrating('error');
            setTtsError(
              'Could not play audio. Please check your audio settings.'
            );
          });
      };

      audio.onpause = () => {
        setOrating('paused');
      };

      audio.onended = () => {
        setOrating(false);
      };

      audio.onerror = () => {
        console.error('Audio loading error');
        setOrating('error');
        setTtsError('Failed to load audio. Please try again.');
        setIsPlaying(false);
      };

      audio.load();
    } catch (err) {
      console.error('TTS error:', err);
      setOrating('error');

      let errorMessage = 'Failed to generate speech';

      if (err instanceof TTSError) {
        errorMessage = err.message;
        try {
          const body = JSON.parse(err.message.split('Body: ')[1]);
          errorMessage = body.message;
        } catch (e) {
          console.log(e);
        }
        // Handle specific error cases
        if (
          errorMessage.includes('token') ||
          errorMessage.includes('authentication')
        ) {
          errorMessage =
            'TTS service is not available. Please try again later.';
        }
      }

      setTtsError(errorMessage);

      // Show error notification
      showNotification({
        title: 'Speech Error',
        message: errorMessage,
        color: 'red',
      });
    }
  };

  if (streaming && (!props?.message || props.message.length === 0)) {
    return (
      <div className="flex items-center space-x-4">
        <Image src={'/icons/favicon.png'} mah={40} maw={40} className="mt-2" />
        <div className="inline-block text-center bg-white border border-gray-200 rounded-tr-lg rounded-b-lg p-3">
          <Loader size={'md'} variant="dots" color="#29748F" />
        </div>
      </div>
    );
  }

  if (isImageMessageJSON(props.message)) {
    const formattedMessage = JSON.parse(props.message);
    return (
      <div className="flex items-start space-x-4">
        <Image
          src={'/icons/favicon.png'}
          mah={40}
          maw={40}
          className="self-start mt-2"
        />
        <div className="flex-1">
          <div className="rounded-lg bg-white border border-gray-200 w-full md:max-w-[500px] p-4">
            <div className="text-sm text-black">
              <ReactMarkdown>{formattedMessage.text}</ReactMarkdown>
              <Tooltip
                label={ttsError || 'Click to speak'}
                position="top"
                withArrow
                opened={!!ttsError}
              >
                <div className="relative">
                  {!orating || orating === 'error' ? (
                    <div className="relative">
                      <VolumeDownRounded
                        className={`w-5 h-5 ${
                          orating === 'error'
                            ? 'text-red-500'
                            : 'text-gray-400 hover:text-[#29748F]'
                        } cursor-pointer`}
                        onClick={togglePlayback}
                      />
                    </div>
                  ) : orating === 'loading' ? (
                    <Loader size={20} />
                  ) : (
                    <SpeakerXMarkIcon
                      className="w-5 h-5 text-[#29748F] cursor-pointer"
                      onClick={() => setOrating(false)}
                    />
                  )}
                  {orating === 'error' && (
                    <ExclamationTriangleIcon className="absolute -top-1 -right-1 w-3 h-3 text-red-500" />
                  )}
                </div>
              </Tooltip>
              {formattedMessage.image_url && (
                <div className="mt-3 border border-gray-200 rounded-lg p-2">
                  <Image
                    src={formattedMessage.image_url}
                    radius="sm"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-row justify-between items-end gap-2">
      <div
        className={`flex flex-col items-end justify-between gap-4 {
          !orating && "mr-14"
        }`}
      >
        <div className=" flex flex-row gap-4">
          <Image
            src={'/icons/favicon.png'}
            mah={40}
            maw={40}
            className="self-center"
          />
          <div className="rounded-lg bg-white border border-gray-200 w-full md:max-w-[500px] p-4">
            <ReactMarkdown>{props.message}</ReactMarkdown>
            {image_url && (
              <div className="mt-3 border border-gray-200 rounded-lg p-2">
                <Image src={image_url} radius="sm" width="400" />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <ActionIcon
            className="hover:text-[#29748F]"
            size={'1rem'}
            onClick={() => handleCopy(props.message)}
          >
            <Icon name="copy" size="1rem" />
          </ActionIcon>
          <ActionIcon
            size="1rem"
            className="min-w-max transition-all duration-300"
            onClick={togglePlayback}
          >
            {orating === 'loading' ? (
              <div className="h-max w-max rounded-full">
                <Loader size={'xs'} className="-ml-[0.2rem]" />
              </div>
            ) : orating === 'playing' ? (
              <div className="flex gap-1 items-center w-max h-max">
                <WaveformAnimation />
                <PauseIcon className="p-0.5 w-4 h-4 text-white rounded-full bg-[#29748F]" />
              </div>
            ) : orating === 'paused' ? (
              <div className="flex gap-1 items-center w-max h-max">
                <SpeakerWaveIcon className="h-4 w-4 -ml-[0.2rem] text-[#29748F]" />
                <PlayIcon className="p-0.5 w-4 h-4 text-white rounded-full bg-[#29748F]" />
              </div>
            ) : (
              <SpeakerWaveIcon className="h-4 w-4 -ml-[0.2rem] text-gray-500 hover:text-[#29748F]" />
            )}
          </ActionIcon>
        </div>
      </div>
    </div>
  );
};

export default BotMessage;
